import { yupResolver } from "@hookform/resolvers/yup";
import React, { RefObject, useEffect, useMemo, useRef, useState } from "react";
import { FieldErrors, Resolver, useForm, useWatch } from "react-hook-form";

import useUserStore from "@store/useUserStore";
import { useSwitches } from "@hooks/useSwitches";
import { useDebounce } from "@hooks/useDebounce";
import { useNoStageLabel } from "@hooks/useNoStageLabel";
import { useFindCompanyByWebsite } from "../hooks/useFindCompanyByWebsite";
import { useCompanySuggestions } from "../hooks/useCompanySuggestions";
import { useCompanyPriorities } from "../hooks/useCompanyPriorities";
import { useCompanyFormInitialization } from "./hooks/useCompanyFormInitialization";
import { useUsers } from "@components/TeamList/hooks/useUsers";
import { useCompanyStages } from "../hooks/useCompanyStages";
import { useDealStages } from "../hooks/useDealStages";
import { useCompanyTypes } from "../hooks/useCompanyTypes";
import { CompanyTypeSubtype } from "@enums/companies.enum";
import { RelationObjectType } from "@enums/relations.enum";
import { Source } from "@enums/common.enum";
import { UserForDropdown } from "@app-types/common.type";
import { ICompany, ISuggestionCompany } from "@interfaces/company.interface";
import { CreateCompanyRequest } from "@app-types/requests.type";
import { UpdateCompanyPayload } from "@screens/company/hooks/useUpdateCompany";
import { IThemeOption } from "@interfaces/sourcing.interface";
import { IAttribute, IAttributeFieldValue } from "@interfaces/attribute.interface";
import { getCompanyTypeOptions, getPriorityOptions, getStageOptions } from "./utils";
import { convertUsersToUsersForDropdown } from "@utils/users.util";
import { addUrlProtocol, getBaseUrl } from "@utils/strings.util";
import { companySchema, IAttributeValidationContext, URL_REGEX } from "../schema";
import { cn } from "@utils/tailwind.util";
import { flattenErrorPaths, scrollToFirstError } from "@utils/formErrors.util";
import { openCompanyPage, openDealPage } from "@utils/navigation.util";
import { IPlatformCompanySelectOption } from "./Selects";
import {
  TeamField,
  IPipelineStageSelectOption,
  PipelineStageFormField,
  SourcingThemeFormField,
} from "@components/AddNewEntity";
import {
  AttributesForm,
  useAllAttributes,
  useCompanyAttributes,
  useDefaultAttributeValues,
  filterAndProcessAttributeValues,
  getCommonAttributeIds,
} from "@components/Attributes";
import { ICompanyTypeSelectOption } from "./Selects";
import { IPrioritySelectOption } from "./Selects";
import { WebsiteDomainsForm } from "./WebsiteDomainsForm/WebsiteDomainsForm";
import { CompanyTypeFormField, PriorityFormField, PlatformCompanyFormField } from "./FormFields";
import { CollapsibleSection } from "@ui/CollapsibleSection";
import { ControlledInput } from "@ui/FormElements";
import { Button } from "@ui/Button";
import { View } from "@ui/View";

export interface ICreateCompanyForm {
  title: string;
  familiar_name: string;
  website: string;
  priority: IPrioritySelectOption | null;
  stage: IPipelineStageSelectOption | null;
  type: ICompanyTypeSelectOption | null;
  users: UserForDropdown[];
  themes: IThemeOption[];
  platform: IPlatformCompanySelectOption | null;
  attribute_values: IAttributeFieldValue[];
}

interface Props {
  company?: ICompany;
  initialTitle?: string;
  initialWebsite?: string;
  sourcePlatform: Source;
  onSubmit: (data: CreateCompanyRequest) => Promise<boolean>;
  onCancel?: VoidFunction;
  onUpdateCompanyFromBrandfetch?: (company: ISuggestionCompany) => void;
  onFieldSave?: (data: Omit<UpdateCompanyPayload, "id">) => void;
  onThemeAdd?: (theme: IThemeOption, removedParentIds: string[]) => void;
  onThemeRemove?: (theme: IThemeOption, addedParentId: string | null) => void;
  hideSecondaryButton?: boolean;
  isSubmitting?: boolean;
  containerClassName?: string;
  headerRef?: RefObject<HTMLElement>;
  // Show the unified company-attributes section (company screen + company creation).
  showAttributes?: boolean;
}

export function CompanyForm({
  company,
  initialTitle,
  initialWebsite,
  sourcePlatform,
  onCancel,
  onSubmit,
  onUpdateCompanyFromBrandfetch,
  onFieldSave,
  onThemeAdd,
  onThemeRemove,
  hideSecondaryButton = false,
  isSubmitting = false,
  containerClassName,
  headerRef,
  showAttributes = false,
}: Props) {
  const { activeUser } = useUserStore();
  const { isAvailable } = useSwitches();
  const titleInputRef = useRef<HTMLInputElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  // The form is its own scroll container; scrollToFirstError needs a handle on it.
  const formRef = useRef<HTMLFormElement>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  // Read by the schema's attribute_values rules; a ref because the attribute list depends on the
  // type selected in this very form, so it isn't known when useForm runs.
  const attributesRef = useRef<IAttribute[]>([]);

  const defaultValues = {
    title: initialTitle || "",
    familiar_name: "",
    website: initialWebsite || "",
    priority: null,
    stage: null,
    type: null,
    users: [],
    themes: [],
    platform: null,
    attribute_values: [],
  };

  const {
    control,
    formState,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    setValue,
    getValues,
    getFieldState,
    trigger,
  } = useForm<ICreateCompanyForm>({
    mode: "all",
    defaultValues,
    reValidateMode: "onChange",
    context: { getAttributes: () => attributesRef.current } as IAttributeValidationContext,
    resolver: yupResolver(companySchema) as Resolver<ICreateCompanyForm>,
  });

  const formData = useWatch({ control });

  const website = getBaseUrl(addUrlProtocol(formData.website));
  const debouncedWebsite = useDebounce(website, 500);
  const isExistingCompany = Boolean(company?.id);
  const isWebsiteValid = !isExistingCompany && URL_REGEX.test(debouncedWebsite);

  const { data: companyLookup, isLoading } = useFindCompanyByWebsite(
    isWebsiteValid ? debouncedWebsite : ""
  );
  const { data: companySuggestions } = useCompanySuggestions(debouncedWebsite);
  const { data: types } = useCompanyTypes();
  const { data: companyStages } = useCompanyStages();
  const { data: dealStages } = useDealStages();
  const { data: priorities } = useCompanyPriorities();
  const { data: allUsers, isLoading: isLoadingUsers } = useUsers();
  const noStageLabel = useNoStageLabel();

  const type = getValues().type;
  const isWebsiteUnique = !companyLookup?.company_id;
  const { error: webSiteError } = getFieldState("website");

  const { data: companyAttributes } = useCompanyAttributes({ type: type?.id });

  useEffect(() => {
    attributesRef.current = companyAttributes;
  }, [companyAttributes]);

  useDefaultAttributeValues<ICreateCompanyForm>({
    name: "attribute_values",
    control,
    setValue,
    attributes: companyAttributes,
    existingValues: company?.attribute_values,
  });

  // Common-attribute link icon: shown only for attributes shared with the company's active deal.
  // When the Multiple Active Deals flag is on the deal lives in `primary_deal` (active_deal is null).
  const { data: allAttributes = [], isLoading: isLoadingAttributes } = useAllAttributes();
  const effectiveDeal =
    (isAvailable("MULTIPLE_ACTIVE_DEALS") ? company?.primary_deal : company?.active_deal) ?? null;
  const commonAttributeIds = useMemo(
    () => (effectiveDeal ? getCommonAttributeIds(allAttributes) : undefined),
    [allAttributes, effectiveDeal]
  );

  const companyTypesOptions = useMemo(() => getCompanyTypeOptions(types), [types]);
  const prioritiesOptions = useMemo(() => getPriorityOptions(priorities), [priorities]);
  const stageOptions = useMemo(
    () =>
      getStageOptions({
        company,
        companyStages,
        dealStages,
        types,
        currentType: type,
        noStageLabel,
      }),
    [
      company?.active_deal,
      company?.stage_object_id,
      companyStages,
      dealStages,
      types,
      type,
      noStageLabel,
    ]
  );
  const userOptions: UserForDropdown[] = useMemo(
    () => convertUsersToUsersForDropdown(allUsers?.results, activeUser),
    [allUsers, activeUser]
  );

  const matchedCompany = companySuggestions?.[0] ?? null;
  const primaryButtonLabel = isExistingCompany ? "View in Meridian" : "Add Company";
  // Attributes gate submit: until they load, the Company Details fields aren't rendered and the
  // resolver has no definitions to check, so a required one would pass unseen.
  const disablePrimaryButton =
    (!isExistingCompany && (isLoading || isLoadingAttributes)) || isSubmitting;
  const isVisiblePlatformCompany = type?.subtype === CompanyTypeSubtype.ADD_ON;

  const handleFormSubmit = async (data: ICreateCompanyForm) => {
    const website = getBaseUrl(addUrlProtocol(data.website));

    const body: CreateCompanyRequest = {
      source: sourcePlatform,
      title: data.title,
      ...(!isExistingCompany && { familiar_name: data.familiar_name }),
      website,
      type: data.type?.id || null,
      stage_object_id: data.stage?.id || null,
      content_type: company?.active_deal ? "deals" : "companies",
      priority: data.priority?.id || null,
      users: data.users?.map((user) => user.id) || [],
      platform_company: data.platform?.value || null,
      theme_ids: data.themes?.map((theme) => theme.id) ?? [],
      attribute_values: filterAndProcessAttributeValues(
        data.attribute_values ?? [],
        companyAttributes
      ),
    };

    const isSuccess = await onSubmit(body);
    if (isSuccess) {
      reset(getValues()); // to keep data as it is but make form clean, to track next changes
    }
  };

  // A required attribute can sit inside the collapsed "Company Details" section, where the error
  // would otherwise be invisible and the failed submit unexplained.
  const handleInvalidSubmit = (errors: FieldErrors<ICreateCompanyForm>) => {
    const errorPaths = flattenErrorPaths(errors);
    const isInDetails = errorPaths[0]?.startsWith("attribute_values");

    scrollToFirstError(
      errorPaths,
      formRef,
      isInDetails ? { isOpen: isDetailsOpen, open: () => setIsDetailsOpen(true) } : undefined
    );
  };

  const handlePrimaryButtonClick = () => {
    if (isExistingCompany) {
      if (company) openCompanyPage(company.id);
    } else {
      if (disablePrimaryButton) return;
      void handleSubmit(handleFormSubmit, handleInvalidSubmit)();
    }
  };

  const handleSecondaryButtonClick = () => {
    reset();
    onCancel?.();
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const target = e.target as HTMLElement;
      const form = target.closest("form");
      const href = form?.querySelector('input[name="href"]') as HTMLInputElement | null;
      href?.focus();
    }
  };

  const handleTitleBlur = () => {
    if (isExistingCompany) {
      const titleValue = formData.title?.trim();
      if (titleValue && titleValue.length >= 3 && titleValue !== company?.title) {
        onFieldSave?.({ title: titleValue });
      }
      return;
    }
    onUpdateCompanyFromBrandfetch?.({ ...matchedCompany, name: formData.title });
  };

  const handleFamiliarNameBlur = () => {
    const value = formData.familiar_name?.trim() ?? "";
    if (isExistingCompany && value !== (company?.familiar_name ?? "")) {
      onFieldSave?.({ familiar_name: value });
    }
  };

  const handleWebsiteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (disablePrimaryButton) return;
      void handleSubmit(handleFormSubmit, handleInvalidSubmit)().catch(() => {});
    }
  };

  useEffect(() => {
    if (!debouncedWebsite) return;
    if (company?.website === website) return;

    if (!isWebsiteUnique && !webSiteError) {
      setError("website", {
        type: "manual",
        message: "This company website already exists",
      });
    } else if (isWebsiteUnique && !webSiteError) {
      clearErrors("website");
      void trigger("website"); // trigger validation for website field
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedWebsite, isWebsiteUnique, setError, clearErrors, trigger, webSiteError]);

  useEffect(() => {
    if (titleInputRef.current) {
      titleInputRef.current.focus({ preventScroll: true });
    }
  }, []);

  // Initialize default values for form fields: type, priority, stage
  useCompanyFormInitialization({
    form: { reset, setValue, getValues },
    query: initialTitle,
    company,
    matchedCompany,
    companyTypesOptions,
    prioritiesOptions,
    stageOptions,
    userOptions,
    onUpdateCompanyFromBrandfetch,
  });

  return (
    <form
      ref={formRef}
      className={cn(
        "h-[calc(100vh_-_78px)] w-full flex flex-col overflow-y-auto",
        containerClassName
      )}
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="flex flex-col gap-4 px-4 flex-1">
        <ControlledInput
          control={control}
          name="title"
          label="Name*"
          onKeyDown={handleTitleKeyDown}
          onBlur={handleTitleBlur}
          errors={formState.errors}
          inputRef={titleInputRef}
        />
        {isExistingCompany && company ? (
          <WebsiteDomainsForm company={company} />
        ) : (
          <ControlledInput
            control={control}
            name="website"
            label="Website*"
            onKeyDown={handleWebsiteKeyDown}
            errors={formState.errors}
          />
        )}
        {isAvailable("FAMILIAR_NAME") && (
          <ControlledInput
            control={control}
            name="familiar_name"
            label="Familiar Name"
            onBlur={handleFamiliarNameBlur}
            errors={formState.errors}
          />
        )}

        {/* Company sourcing themes */}
        {isAvailable("THEMATIC_SOURCING") && (
          <SourcingThemeFormField<ICreateCompanyForm>
            control={control}
            setValue={setValue}
            defaultValue={company?.themes || []}
            relatedEntityName={company?.title}
            onThemeAdd={isExistingCompany ? onThemeAdd : undefined}
            onThemeRemove={isExistingCompany ? onThemeRemove : undefined}
          />
        )}

        {/* Company priority */}
        <PriorityFormField
          control={control}
          options={prioritiesOptions}
          onSave={
            isExistingCompany
              ? (option) => onFieldSave?.({ priority: option?.id || null })
              : undefined
          }
        />

        {/* Company type */}
        <CompanyTypeFormField
          control={control}
          options={companyTypesOptions}
          onSave={
            isExistingCompany ? (option) => onFieldSave?.({ type: option?.id || null }) : undefined
          }
        />

        {/* Company platform */}
        <View.Condition if={isVisiblePlatformCompany}>
          <PlatformCompanyFormField
            control={control}
            onSave={
              isExistingCompany
                ? (option) => onFieldSave?.({ platform_company: option?.value || null })
                : undefined
            }
          />
        </View.Condition>

        {/* Company stage */}
        <PipelineStageFormField<ICreateCompanyForm>
          control={control}
          options={stageOptions}
          showGroupHeaders={false}
          onSave={
            isExistingCompany
              ? (option) =>
                  onFieldSave?.({
                    stage_object_id: option?.id || null,
                    content_type: company?.active_deal ? "deals" : "companies",
                  })
              : undefined
          }
        />

        {/* Company users */}
        <TeamField
          control={control}
          options={userOptions}
          label="Coverage Team"
          hideSelectAllOption
          onSave={
            isExistingCompany
              ? (members) => onFieldSave?.({ users: members.map((m) => m.id) })
              : undefined
          }
          isLoading={isLoadingUsers}
          headerRef={headerRef}
          footerRef={footerRef}
        />

        {/* Unified company attributes — filtered by the selected company type */}
        {showAttributes && companyAttributes.length > 0 && (
          <CollapsibleSection
            title="Company Details"
            className="mb-4"
            isOpen={isDetailsOpen}
            onOpenChange={setIsDetailsOpen}
          >
            <AttributesForm<ICreateCompanyForm>
              name="attribute_values"
              control={control}
              attributes={companyAttributes}
              contentType={RelationObjectType.COMPANY}
              currency={company?.currency}
              commonAttributeIds={commonAttributeIds}
              // A company can't be tagged into its own Data Objects field.
              excludedRelationIds={company ? [company.id] : undefined}
              onCommonAttributeClick={
                effectiveDeal ? () => openDealPage(effectiveDeal.id) : undefined
              }
              savedValues={company?.attribute_values}
              onAttributeSave={
                isExistingCompany
                  ? (value, isCommon) =>
                      onFieldSave?.({
                        hasSharedAttributeUpdates: isCommon,
                        attribute_values: [
                          filterAndProcessAttributeValues(value, companyAttributes, {
                            allowEmptyValues: true,
                          }),
                        ],
                      })
                  : undefined
              }
            />
          </CollapsibleSection>
        )}
      </div>

      <div
        ref={footerRef}
        className="sticky bottom-0 flex justify-between border-t-2 border-stroke-primary dark:border-gray-600 pt-4 px-4 bg-white dark:bg-gray-800"
      >
        <div className="w-full flex flex-col gap-2">
          <Button
            disabled={disablePrimaryButton}
            onClick={handlePrimaryButtonClick}
            buttonType="PRIMARY"
          >
            {primaryButtonLabel}
          </Button>

          <View.Condition if={!hideSecondaryButton && !isExistingCompany}>
            <Button
              className="disabled:bg-white80 disabled:opacity-50 dark:disabled:hover:bg-gray-700"
              disabled={isSubmitting}
              onClick={handleSecondaryButtonClick}
              buttonType="CANCEL"
            >
              Cancel
            </Button>
          </View.Condition>
        </div>
      </div>
    </form>
  );
}
